<!-- ===== Header bar ===== -->
<header class="app-header">
  <div class="brand">
    <span class="brand-mark">H</span>
    <span class="brand-name">Harrell's</span>
    <span class="brand-app">Doc Repo</span>
  </div>
  @if (canDownloadManualChecks) {
    <span class="readonly-pill download-pill" role="button" tabindex="0"
          [class.loading]="manualChecksLoading"
          (click)="onDownloadManualChecks()" (keyup.enter)="onDownloadManualChecks()">
      ● {{ manualChecksLoading ? 'DOWNLOADING MANUAL CHECKS. PLEASE WAIT…' : 'DOWNLOAD MANUAL CHECKS' }}
    </span>
  } @else {
    <span class="readonly-pill">● READ-ONLY</span>
  }
</header>

<!-- Reusable manual-checks message/confirm modal (replaces window.alert / confirm). -->
@if (mcModalOpen) {
  <div class="mc-overlay">
    <div class="mc-picker">
      <div class="mc-modal-msg">{{ mcModalMessage }}</div>
      <div class="mc-picker-actions">
        @if (mcModalConfirm) {
          <button type="button" class="mc-btn mc-btn-cancel" (click)="mcModalCancel()">Cancel</button>
        }
        <button type="button" class="mc-btn mc-btn-ok" (click)="mcModalOk()">OK</button>
      </div>
    </div>
  </div>
}

<!-- Blocks the whole screen while a manual-check import is running. -->
@if (manualChecksLoading) {
  <div class="mc-overlay">
    <div class="mc-overlay-msg">Downloading manual checks. Please wait…</div>
  </div>
}

@if (!accessChecked) {
  <div class="access-state">Checking access…</div>
} @else if (accessDenied) {
  <div class="access-denied">
    <span class="material-icons big">lock</span>
    <h2>You do not have access to this application.</h2>
    <p class="muted">Contact IT if you believe you should have access to the Doc Repo.</p>
  </div>
} @else {

<!-- ===== Filter bar ===== -->
<section class="filter-bar">
  <div class="filter">
    <label>COMPANY <span class="req">*</span></label>
    <select [(ngModel)]="filters.company" (ngModelChange)="onCompanyChange()"
            [class.needs-value]="!filters.company">
      <option value="" disabled>Select…</option>
      @for (c of companies; track c.value) {
        <option [value]="c.value">{{ c.label }}</option>
      }
    </select>
  </div>

  <div class="filter">
    <label>CATEGORY <span class="req">*</span></label>
    <select [(ngModel)]="filters.category" (ngModelChange)="onCategoryChange()" [disabled]="!filters.company"
            [class.needs-value]="!filters.category">
      <option value="" disabled>Select…</option>
      @for (c of categories; track c.value) {
        <option [value]="c.value">{{ c.label }}</option>
      }
    </select>
  </div>

  <div class="filter">
    <label>CLASSIFICATION <span class="req">*</span></label>
    <div class="ms-dropdown">
      <button type="button" class="ms-toggle"
              [class.needs-value]="selectedClassificationIds.length === 0 && !showComingSoon"
              [class.open]="classificationMenuOpen"
              [disabled]="!filters.company || showComingSoon"
              (click)="toggleClassificationMenu()">
        <span class="ms-summary">{{ classificationSummary }}</span>
        <span class="ms-caret">▾</span>
      </button>
      @if (classificationMenuOpen) {
        <div class="ms-panel">
          @for (c of classifications; track c.value) {
            <label class="ms-item">
              <input type="checkbox" [checked]="isClassificationSelected(c.value)"
                     (change)="toggleClassification(c.value)" />
              <span>{{ c.label }}</span>
            </label>
          }
        </div>
      }
    </div>
  </div>

  <div class="filter grow">
    <label>{{ partyLabel }}</label>
    <div class="combo">
      <input type="text" autocomplete="off"
             [(ngModel)]="vendorInputText"
             [disabled]="!filters.company"
             [placeholder]="filters.company ? partyPlaceholder : 'Select a company first'"
             (focus)="vendorFocus()"
             (keyup)="filterVendors($event)" />
      @if (showVendorList) {
        <ul class="combo-list">
          <li class="combo-clear" (click)="clearVendor()">clear</li>
          @for (v of vendorsFiltered; track v.value) {
            <li class="combo-option" (click)="vendorSelected(v)">
              <span class="combo-code">{{ v.value }}</span>
              <span class="combo-desc">{{ v.label }}</span>
            </li>
          } @empty {
            <li class="combo-empty">No matching {{ partyLabel.toLowerCase() }}s</li>
          }
        </ul>
      }
    </div>
  </div>

  @if (showApprover) {
    <div class="filter grow">
      <label>APPROVER</label>
      <div class="combo">
        <input type="text" autocomplete="off"
               [(ngModel)]="approverInputText"
               [disabled]="!filters.company"
               [placeholder]="filters.company ? 'Search approver…' : 'Select a company first'"
               (focus)="approverFocus()"
               (keyup)="filterApprovers($event)" />
        @if (showApproverList) {
          <ul class="combo-list">
            <li class="combo-clear" (click)="clearApprover()">clear</li>
            @for (a of approversFiltered; track a.value) {
              <li class="combo-option" (click)="approverSelected(a)">
                <span class="combo-code">{{ a.value }}</span>
              </li>
            } @empty {
              <li class="combo-empty">No matching approvers</li>
            }
          </ul>
        }
      </div>
    </div>
  }

  @if (showGlAcct) {
  <div class="filter grow">
    <label>GL ACCT</label>
    <div class="combo">
      <input type="text" autocomplete="off"
             [(ngModel)]="glInputText"
             [disabled]="!filters.company"
             [placeholder]="filters.company ? 'Search code or description…' : 'Select a company first'"
             (focus)="glFocus()"
             (keyup)="filterGl($event)" />
      @if (showGlList) {
        <ul class="combo-list">
          <li class="combo-clear" (click)="clearGl()">clear</li>
          @for (g of glFiltered; track g.value) {
            <li class="combo-option" (click)="glSelected(g)">
              <span class="combo-code">{{ g.value }}</span>
              <span class="combo-desc">{{ g.label }}</span>
            </li>
          } @empty {
            <li class="combo-empty">No matching GL accounts</li>
          }
        </ul>
      }
    </div>
  </div>
  }

  <div class="filter">
    <label>DATE FROM <span class="req" [class.dim]="isCheckLookup">*</span></label>
    <input type="date" [(ngModel)]="filters.dateFrom" [class.needs-value]="!filters.dateFrom && !isCheckLookup" />
  </div>

  <div class="filter">
    <label>DATE TO <span class="req" [class.dim]="isCheckLookup">*</span></label>
    <input type="date" [(ngModel)]="filters.dateTo" [class.needs-value]="!filters.dateTo && !isCheckLookup" />
  </div>

  <div class="filter compact">
    <label>AMOUNT</label>
    <input type="text" inputmode="decimal" [(ngModel)]="filters.amount" placeholder="e.g. 123.45" />
  </div>

  @if (isManualCheckClassification) {
    <div class="filter compact">
      <label>CHECK #</label>
      <input type="text" [(ngModel)]="filters.checkNumber" placeholder="e.g. 123456"
             (keyup.enter)="refresh()" />
    </div>
  }

  <div class="filter compact">
    <label>DOC #</label>
    <input type="text" [(ngModel)]="filters.docNumber" placeholder="e.g. INV123456" />
  </div>

  <div class="filter compact">
    <label>PO #</label>
    <input type="text" [(ngModel)]="filters.poNumber" placeholder="e.g. PO-12345" />
  </div>

  <div class="filter-actions">
    <button class="btn-clear-filters" (click)="clearFilters()" title="Clear all filters">
      <span class="material-icons">close</span> CLEAR
    </button>
    <button class="btn-refresh" [disabled]="!canRefresh || loading || showComingSoon" (click)="refresh(1)"
            [title]="canRefresh ? 'Refresh' : 'Required: ' + missingFilters.join(', ')">
      <span class="material-icons">refresh</span> REFRESH
    </button>
    @if (result && !showComingSoon) {
      <div class="doc-count"><strong>{{ result.totalCount | number }}</strong> DOCUMENTS</div>
    }
  </div>
</section>

@if (!canRefresh && !showComingSoon) {
  <div class="hint-row">
    Select <strong>Company</strong>, <strong>Classification</strong> and both <strong>dates</strong> before refreshing.
  </div>
}

<!-- ===== Body: table + PDF pane ===== -->
<div class="body" [class.pane-open]="paneOpen">
  <div class="grid-wrap">
    @if (showComingSoon) {
      <div class="coming-soon">
        <span class="material-icons big">hourglass_empty</span>
        <h2>{{ filters.category }} documents — coming soon</h2>
        <p class="muted">The {{ filters.category }} viewer isn't available yet. Select the <strong>AP</strong> category to browse documents.</p>
      </div>
    } @else {
    @if (error) {
      <div class="error-box">{{ error }}</div>
    }

    <table class="doc-table">
      <thead>
        <tr>
          <th class="col-vendor sortable" (click)="sort('vendorId')">{{ partyLabel }} #<span class="sort-ind">{{ sortIndicator('vendorId') }}</span></th>
          <th class="sortable" (click)="sort('vendorName')">{{ partyLabel }} NAME<span class="sort-ind">{{ sortIndicator('vendorName') }}</span></th>
          <th class="sortable" (click)="sort('classification')">CLASSIFICATION<span class="sort-ind">{{ sortIndicator('classification') }}</span></th>
          <th class="sortable" (click)="sort('docNumber')">DOC #<span class="sort-ind">{{ sortIndicator('docNumber') }}</span></th>
          <th class="col-date sortable" (click)="sort('docDate')">DATE<span class="sort-ind">{{ sortIndicator('docDate') }}</span></th>
          <th class="col-amount sortable" (click)="sort('amount')">AMOUNT<span class="sort-ind">{{ sortIndicator('amount') }}</span></th>
          <th class="col-po sortable" (click)="sort('poNumber')">PO #<span class="sort-ind">{{ sortIndicator('poNumber') }}</span></th>
          <th class="col-sup sortable" (click)="sort('supDoc')">SUP DOC<span class="sort-ind">{{ sortIndicator('supDoc') }}</span></th>
        </tr>
      </thead>
      <tbody>
        @if (loading) {
          <tr><td colspan="8" class="state-cell">Loading…</td></tr>
        } @else if (result && result.items.length === 0) {
          <tr><td colspan="8" class="state-cell">No documents match your filters.</td></tr>
        } @else if (!hasSearched) {
          <tr><td colspan="8" class="state-cell muted">Set your filters and click Refresh to load documents.</td></tr>
        }

        @for (doc of result?.items || []; track trackById($index, doc)) {
          <tr class="doc-row" [class.expanded]="expandedId === doc.azureDocId" (click)="toggleRow(doc)">
            <td class="col-vendor">{{ doc.vendorId }}</td>
            <td>{{ doc.vendorName }}</td>
            <td>{{ classificationLabel(doc.classification) }}</td>
            <td>{{ doc.docNumber }}</td>
            <td class="col-date">{{ doc.docDate | date: 'yyyy-MM-dd' }}</td>
            <td class="col-amount">{{ doc.amount | currency }}</td>
            <td class="col-po">{{ doc.poNumber || '—' }}</td>
            <td class="col-sup">
              @if (doc.supDocCount > 0) {
                <span class="sup-badge">{{ doc.supDocCount }}</span>
              } @else { — }
            </td>
          </tr>

          @if (expandedId === doc.azureDocId) {
            <tr class="detail-row">
              <td colspan="8">
                @if (detailLoading && !details[doc.azureDocId]) {
                  <div class="detail-loading">Loading detail…</div>
                }
                @if (details[doc.azureDocId]; as d) {
                  <div class="detail">
                    @if (!isAr) {
                      <div class="detail-head">
                        <div class="field"><label>AP APPROVER</label><span>{{ d.apApproverName || '—' }}</span></div>
                        <div class="field"><label>APPROVAL DATE</label><span>{{ (d.approvedDate | date: 'yyyy-MM-dd') || '—' }}</span></div>
                        <div class="field"><label>CHECK #</label><span>{{ d.checkNumber || '—' }}</span></div>
                        <div class="field"><label>CHECK AMOUNT</label><span>{{ d.checkAmount != null ? (d.checkAmount | currency) : '—' }}</span></div>
                        <div class="field"><label>CHECK DATE</label><span>{{ (d.checkDate | date: 'yyyy-MM-dd') || '—' }}</span></div>
                        <div class="field pdf-link">
                          @if (d.files.length > 1) {
                            <label>DOCUMENTS ({{ d.files.length }})</label>
                            <div class="file-buttons">
                              @for (f of d.files; track f.sequenceNum; let i = $index) {
                                <button class="link-btn" [class.active]="isActiveFile(doc.azureDocId, i)"
                                        [title]="f.fileName"
                                        (click)="openPane(doc, i); $event.stopPropagation()">
                                  <span class="material-icons">{{ fileIcon(f.fileName) }}</span> {{ fileSourceLabel(f) }} — {{ fileTypeLabel(f.fileName) }}
                                </button>
                              }
                            </div>
                          } @else {
                            <button class="link-btn" [class.active]="isActiveFile(doc.azureDocId, 0)"
                                    (click)="openPane(doc, 0); $event.stopPropagation()">
                              <span class="material-icons">{{ fileIcon(d.fileName) }}</span> View Document
                            </button>
                          }
                        </div>
                      </div>

                      @if (d.glLines.length) {
                        <table class="gl-table">
                          <thead>
                            <tr><th class="tcol-a">GL ACCOUNT</th><th class="tcol-b">GL DESCRIPTION</th><th class="col-amount">GL AMOUNT</th></tr>
                          </thead>
                          <tbody>
                            @for (gl of d.glLines; track gl.lineNumber) {
                              <tr>
                                <td>{{ gl.glCode }}</td>
                                <td>{{ gl.glDescription }}</td>
                                <td class="col-amount">{{ gl.glAmount | currency }}</td>
                              </tr>
                            }
                          </tbody>
                        </table>
                      } @else {
                        <div class="no-gl muted">No GL coding lines for this document.</div>
                      }
                    } @else {
                      <!-- AR: minimal detail - just the document(s): invoice PDF + any grouped Return/CreditMemo -->
                      <div class="detail-head">
                        <div class="field pdf-link">
                          @if (d.files.length > 1) {
                            <label>DOCUMENTS ({{ d.files.length }})</label>
                            <div class="file-buttons">
                              @for (f of d.files; track f.sequenceNum; let i = $index) {
                                <button class="link-btn" [class.active]="isActiveFile(doc.azureDocId, i)"
                                        [title]="f.fileName"
                                        (click)="openPane(doc, i); $event.stopPropagation()">
                                  <span class="material-icons">{{ fileIcon(f.fileName) }}</span> {{ fileSourceLabel(f) }} — {{ fileTypeLabel(f.fileName) }}
                                </button>
                              }
                            </div>
                          } @else {
                            <button class="link-btn" [class.active]="isActiveFile(doc.azureDocId, 0)"
                                    (click)="openPane(doc, 0); $event.stopPropagation()">
                              <span class="material-icons">{{ fileIcon(d.fileName) }}</span> View Document
                            </button>
                          }
                        </div>
                      </div>
                    }

                    @if (d.approvalComments.length) {
                      <div class="notes-section approval-comments-section">
                        <label>APPROVAL COMMENTS</label>
                        <table class="gl-table notes-table">
                          <thead>
                            <tr>
                              <th class="tcol-a c-date">DATE</th>
                              <th class="tcol-b c-user">USER</th>
                              <th>COMMENT</th>
                            </tr>
                          </thead>
                          <tbody>
                            @for (c of d.approvalComments; track $index) {
                              <tr>
                                <td class="c-date">{{ c.noteEnteredUtc | date: 'M/d/yyyy h:mm a' }}</td>
                                <td class="c-user">{{ c.authorUsername }}</td>
                                <td class="c-note">{{ c.noteText }}</td>
                              </tr>
                            }
                          </tbody>
                        </table>
                      </div>
                    }

                    @if (d.notes.length) {
                      <div class="notes-section">
                        <table class="gl-table notes-table">
                          <thead>
                            <tr>
                              <th class="tcol-a c-date">DATE</th>
                              <th class="tcol-b c-user">USER</th>
                              <th>NOTE</th>
                            </tr>
                          </thead>
                          <tbody>
                            @for (n of d.notes; track $index) {
                              <tr>
                                <td class="c-date">{{ n.noteEnteredUtc | date: 'M/d/yyyy h:mm a' }}</td>
                                <td class="c-user">{{ n.authorUsername }}</td>
                                <td class="c-note">{{ n.noteText }}</td>
                              </tr>
                            }
                          </tbody>
                        </table>
                      </div>
                    }
                  </div>
                }
              </td>
            </tr>
          }
        }
      </tbody>
    </table>

    @if (result && result.totalPages > 1) {
      <div class="pager">
        <button (click)="prevPage()" [disabled]="result.page <= 1">‹ Prev</button>
        <span>{{ pageStart | number }}–{{ pageEnd | number }} of {{ result.totalCount | number }}</span>
        <button (click)="nextPage()" [disabled]="result.page >= result.totalPages">Next ›</button>
      </div>
    }
    }
  </div>

  <!-- ===== PDF preview pane ===== -->
  @if (paneOpen && paneDoc) {
    <aside class="pdf-pane">
      <div class="pane-header">
        <span class="pane-title">{{ paneDoc.vendorName }} — {{ paneDoc.invoiceNumber }}</span>
        <div class="pane-actions">
          <button class="btn-open" (click)="openInTab()" title="Open in a new browser tab">
            <span class="material-icons">open_in_new</span> OPEN
          </button>
          <button class="btn-download" (click)="download()">DOWNLOAD</button>
          <button class="btn-email" (click)="email()" [class.active]="emailFormOpen">EMAIL</button>
          <button class="btn-close" (click)="closePane()"><span class="material-icons">close</span></button>
        </div>
      </div>

      <!-- Inline "email this document" form -->
      @if (emailFormOpen) {
        <form class="email-form" (ngSubmit)="sendEmail()">
          <label class="email-field">
            <span>Send to</span>
            <input type="email" name="emailTo" [(ngModel)]="emailTo"
                   placeholder="name@example.com" autocomplete="off" [disabled]="emailSending" />
          </label>
          <label class="email-field">
            <span>Message <em>(optional)</em></span>
            <textarea name="emailMessage" rows="2" [(ngModel)]="emailMessage"
                      placeholder="Add a note…" [disabled]="emailSending"></textarea>
          </label>
          @if (emailError) { <p class="email-error">{{ emailError }}</p> }
          <div class="email-actions">
            <button type="button" class="btn-cancel" (click)="cancelEmail()" [disabled]="emailSending">CANCEL</button>
            <button type="submit" class="btn-send" [disabled]="emailSending">
              {{ emailSending ? 'SENDING…' : 'SEND' }}
            </button>
          </div>
        </form>
      }
      @if (emailSent) {
        <p class="email-success"><span class="material-icons">check_circle</span> Document emailed.</p>
      }
      <div class="pane-body" [class.doc-bg]="paneFileKind === 'pdf' || paneFileKind === 'text'">
        @switch (paneFileKind) {
          @case ('pdf') {
            <iframe class="doc-frame" [src]="paneSafeUrl" title="Document preview"></iframe>
          }
          @case ('text') {
            <iframe class="doc-frame" [src]="paneSafeUrl" title="Document preview"></iframe>
          }
          @case ('xmlform') {
            @if (paneForm) {
              <div class="xml-form">
                @if (paneForm.fields.length) {
                  <dl class="xf-fields">
                    @for (f of paneForm.fields; track f.label) {
                      <div class="xf-row"><dt>{{ f.label }}</dt><dd>{{ f.value }}</dd></div>
                    }
                  </dl>
                }
                @for (t of paneForm.tables; track t.title) {
                  <div class="xf-table-wrap">
                    <h4 class="xf-table-title">{{ t.title }}</h4>
                    <table class="xf-table">
                      <thead>
                        <tr>@for (c of t.columns; track c) { <th>{{ c }}</th> }</tr>
                      </thead>
                      <tbody>
                        @for (r of t.rows; track $index) {
                          <tr>@for (c of t.columns; track c) { <td>{{ r[c] || '—' }}</td> }</tr>
                        }
                      </tbody>
                    </table>
                  </div>
                }
              </div>
            }
          }
          @case ('image') {
            <img class="doc-image" [src]="paneRawUrl" alt="Document preview" />
          }
          @case ('tiff') {
            <div class="pane-placeholder">
              <span class="material-icons big">image</span>
              <p>TIFF images can't be previewed in the browser.</p>
              <button class="btn-download" (click)="download()">DOWNLOAD TO VIEW</button>
              @if (paneFileName) { <p class="filename">{{ paneFileName }}</p> }
            </div>
          }
          @case ('none') {
            <div class="pane-placeholder">
              <span class="material-icons big">description</span>
              <p>No file is available for this document.</p>
              <p class="muted">The file may still be pending upload.</p>
            </div>
          }
          @default {
            <div class="pane-placeholder">
              <span class="material-icons big">insert_drive_file</span>
              <p>This file type can't be previewed here.</p>
              <button class="btn-download" (click)="download()">DOWNLOAD</button>
              @if (paneFileName) { <p class="filename">{{ paneFileName }}</p> }
            </div>
          }
        }
      </div>
    </aside>
  }
</div>
}
